import React from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { useOrg } from "../contexts/OrganizationContext";
import { useBodyClass } from "../hooks/useBodyClass";
import { useContentTop } from "../hooks/useContentTop";
import CartList from "../components/Cart/CartList";
import ShareCartForm from "../components/Cart/ShareCartForm";
import MapContainer from "../components/Map/MapContainer";
import { parseLatLng } from "../utils/geo";

function CartPage() {
  const { items, itemCount } = useCart();
  const { organization } = useOrg();
  const navigate = useNavigate();

  useBodyClass("page-shoppingCart");
  useContentTop();

  React.useEffect(() => {
    const contentRegion = document.getElementById("content-region");
    if (contentRegion) contentRegion.classList.add("split-content-region");
    return () => {
      if (contentRegion) contentRegion.classList.remove("split-content-region");
    };
  }, []);

  const [selectedItemId, setSelectedItemId] = React.useState(null);
  const [showShare, setShowShare] = React.useState(false);
  // Set only when selection originates from a marker click, so hovering a list
  // item never triggers a scroll. Cleared once the target is scrolled into view.
  const [scrollTargetId, setScrollTargetId] = React.useState(null);
  const itemRefs = React.useRef({});

  const mapCenter = organization.serviceLocale?.geo?.mapCenter || { lat: 41.6, lng: -72.7 };

  const mapResults = React.useMemo(
    () =>
      items
        .map((item) => ({
          ...item,
          latLng: item.latLng || parseLatLng(item.location),
          addressLine: [item.address_1, item.address_2, item.city, item.state]
            .filter(Boolean)
            .join(", "),
        }))
        .filter((item) => item.latLng && item.latLng.lat && item.latLng.lng),
    [items]
  );

  function handleSelectItem(item) {
    setSelectedItemId(item?.program_site_id || item?.id);
  }

  function handleMarkerClick(item) {
    const itemId = item?.program_site_id || item?.id;
    setSelectedItemId(itemId);
    setScrollTargetId(itemId);
  }

  function registerItemRef(itemId, node) {
    if (node) itemRefs.current[itemId] = node;
    else delete itemRefs.current[itemId];
  }

  React.useEffect(() => {
    if (scrollTargetId == null) return;
    const node = itemRefs.current[scrollTargetId];
    if (node) node.scrollIntoView({ behavior: "smooth", block: "nearest" });
    setScrollTargetId(null);
  }, [scrollTargetId]);

  function handleNavigateToItem(item) {
    navigate(`/search/${item.program_site_id}/cart`);
  }

  function handleBack() {
    const lastSearchUrl = sessionStorage.getItem("lastSearchUrl");
    if (lastSearchUrl) {
      navigate(lastSearchUrl);
    } else {
      navigate("/");
    }
  }

  return (
    <div className="shopping-cart-layout">
      <MapContainer
        center={mapCenter}
        results={mapResults}
        selectedResultId={selectedItemId}
        highlightedResultId={selectedItemId}
        onMarkerClick={handleMarkerClick}
      />

      <div className="sidebar">
        <div className="sidebar-header cart-header">
          My Resource List
        </div>

        <div className="results-content">
          <button className="btn btn-default back-to-last-search pointer" onClick={handleBack}>
            <i className="ico ico-arrowright" /> Back
          </button>

          <div className="actions">
            <span
              className="share share-cart small-label pointer"
              onClick={() => setShowShare(!showShare)}
              role="button"
              tabIndex={0}
            >
              <i className="ico ico-share" /> Print &amp; Share
            </span>
          </div>

          {showShare && (
            <ShareCartForm onClose={() => setShowShare(false)} />
          )}

        </div>

        <div className="results-region shopping-cart">
          <CartList
            items={items}
            onSelectItem={handleSelectItem}
            onNavigateItem={handleNavigateToItem}
            selectedItemId={selectedItemId}
            registerItemRef={registerItemRef}
          />
        </div>
      </div>
    </div>
  );
}

export default CartPage;
